/*
 *  Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
 *
 *  Licensed under the Apache License, Version 2.0 (the "License").
 *  You may not use this file except in compliance with the License.
 *  A copy of the License is located at
 *
 *  http://aws.amazon.com/apache2.0
 *
 *  or in the "license" file accompanying this file. This file is distributed
 *  on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 *  express or implied. See the License for the specific language governing
 *  permissions and limitations under the License.
 */

/**
 * The External_Scheduler's idle rule, ported so the scheduling modal can say how a workspace's recent
 * activity would have been judged under the settings being chosen.
 *
 * This is a copy of `signal_is_idle` and `evaluate` in the ec2-scheduler repository's
 * `lambda/idle_handler.py`. It is used for an estimate shown to users, never to decide anything, but an
 * estimate that disagrees with the scheduler would mislead them, so keep the two in step. The tests in
 * `__tests__/idle-rule.test.js` mirror the scheduler's own cases for the parts ported here.
 *
 * Points are `{ timestamp, value }`, ascending, as the idle-activity endpoint returns them.
 */

// The scheduler's metric period, and the gap allowance and tolerance it applies by default. Service
// Workbench never writes IdleStopTolerance, so every workspace it manages uses the default.
const PERIOD_MS = 5 * 60 * 1000;
const ALLOWED_GAP_PERIODS = 2;
const DEFAULT_TOLERANCE = 1;

const toMs = timestamp => new Date(timestamp).getTime();

/**
 * One signal over its trailing window. `window` is newest-first `{ ms, value }`, exactly
 * `requiredPoints` long or shorter.
 *
 * @returns {boolean|null} true idle, false busy, null not enough usable data
 */
function signalIsIdle(window, threshold, requiredPoints, tolerance = DEFAULT_TOLERANCE, periodMs = PERIOD_MS) {
  if (window.length < requiredPoints) return null;

  const points = window.slice(0, requiredPoints);

  // A window spanning a gap straddles a stop, so the readings either side are not judged together.
  if (requiredPoints > 1) {
    const span = points[0].ms - points[points.length - 1].ms;
    if (span > (requiredPoints - 1 + ALLOWED_GAP_PERIODS) * periodMs) return null;
  }

  // Comparison is strictly greater than, so a value exactly at the threshold is quiet.
  if (points[0].value > threshold) return false;

  const exceedances = points.filter(point => point.value > threshold).length;
  return exceedances <= tolerance;
}

/**
 * CPU and GPU combined: idle needs CPU idle and, where GPU data exists, GPU idle too. Missing or
 * too-short GPU data falls back to CPU alone, as the scheduler does.
 */
function evaluate({ cpuWindow, gpuWindow }, { requiredPoints, cpuThreshold, gpuThreshold, tolerance }) {
  const cpu = signalIsIdle(cpuWindow, cpuThreshold, requiredPoints, tolerance);
  if (cpu !== true) return cpu;

  if (!gpuWindow || gpuWindow.length === 0) return true;

  const gpu = signalIsIdle(gpuWindow, gpuThreshold, requiredPoints, tolerance);
  return gpu === null ? true : gpu;
}

function normalise(points) {
  return (points || [])
    .map(point => ({ ms: toMs(point.timestamp), value: Number(point.value) }))
    .filter(point => Number.isFinite(point.ms) && Number.isFinite(point.value))
    .sort((a, b) => a.ms - b.ms);
}

/**
 * Replays the rule over a run of history and reports the stops it would have made.
 *
 * With no warning phase, an instance is stopped the first time a check finds it idle. Real history
 * kept running where a stop would have happened, so after each simulated stop the instance is treated
 * as off until the next sign of activity, the moment a user would have started it again, which is how
 * the scheduler's `replay_idle.py` models it. The scheduler checks every 15 minutes; checking every
 * datapoint instead moves a stop by at most 10 minutes and does not change how many there are.
 *
 * @param {{ cpu: Array, gpu: Array }} recent 5-minute series from the idle-activity endpoint
 * @param {{ minutes: number, cpuThreshold: number, gpuThreshold: number, tolerance: number }} settings
 * @returns {{ stops: string[], coveredHours: number }|null} null when there is
 *          nothing to replay (no timeout, no threshold, or no data)
 */
function estimateStops(recent, { minutes, cpuThreshold, gpuThreshold, tolerance = DEFAULT_TOLERANCE } = {}) {
  if (!Number.isFinite(minutes) || minutes <= 0 || !Number.isFinite(cpuThreshold)) return null;

  const cpu = normalise(recent && recent.cpu);
  if (cpu.length === 0) return null;
  const gpu = normalise(recent && recent.gpu);

  const requiredPoints = Math.max(1, Math.floor(minutes / 5));
  const settings = { requiredPoints, cpuThreshold, gpuThreshold, tolerance };

  const stops = [];
  let gpuStart = 0;
  let resumeAt = 0;

  for (let i = 0; i < cpu.length; i += 1) {
    if (i >= resumeAt) {
      const cpuWindow = cpu.slice(Math.max(0, i - requiredPoints + 1), i + 1).reverse();
      const oldest = cpuWindow[cpuWindow.length - 1].ms;
      const newest = cpuWindow[0].ms;

      // GPU points inside the same span, newest first. `gpuStart` only moves forward.
      while (gpuStart < gpu.length && gpu[gpuStart].ms < oldest) gpuStart += 1;
      const gpuWindow = [];
      for (let j = gpuStart; j < gpu.length && gpu[j].ms <= newest; j += 1) gpuWindow.unshift(gpu[j]);

      if (evaluate({ cpuWindow, gpuWindow }, settings) === true) {
        stops.push(new Date(cpu[i].ms).toISOString());

        // Off until activity resumes.
        let next = i + 1;
        while (next < cpu.length && cpu[next].value <= cpuThreshold) next += 1;
        resumeAt = next;
      }
    }
  }

  // How much running history the estimate covers, so the UI can say when it rests on very little.
  return { stops, coveredHours: (cpu.length * 5) / 60 };
}

export { signalIsIdle, evaluate, estimateStops, DEFAULT_TOLERANCE };
